import type { ReactNode } from 'react';
import type { FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { cn } from '~/lib/cn';
import { useFieldIsRequired } from '~settings/components/Form';

import { FieldMessage } from '../FieldMessage';
import { Label } from '../Label';
import { assertNonEmptyName } from './assertions/nonEmptyName';

export interface FormFieldProps<TFieldValues extends FieldValues> {
  name: Path<TFieldValues>;
  label: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  children: ReactNode;

  /**
   * Disables the label HTML tag and uses span instead.
   * Useful when the inner content contains its own labels or multiple inputs.
   *
   * @default false
   */
  labelAsSpan?: boolean;
}

function FormField<TFieldValues extends FieldValues>({
  name,
  label,
  disabled,
  readOnly,
  className,
  children,
  labelAsSpan,
}: FormFieldProps<TFieldValues>) {
  assertNonEmptyName(name);

  const {
    fieldState: { error },
  } = useController<TFieldValues>({ name, disabled });

  const isRequired = useFieldIsRequired(name);

  return (
    <div className={cn('flex', 'flex-col', 'gap-sm', className)}>
      <Label
        id={name}
        disabled={disabled || readOnly}
        asSpan={labelAsSpan}
        name={name}
        className={cn(isRequired && 'after:ml-xs after:content-["*"]')}
      >
        {label}
      </Label>
      {children}
      {error ? (
        <FieldMessage state="invalid">{error.message}</FieldMessage>
      ) : null}
    </div>
  );
}

FormField.displayName = 'FormField';
export { FormField };
